import { useQueryClient } from '@tanstack/react-query';
import {
  getGetApiV5FilesPendingReviewQueryKey,
  usePutApiV5FilesIdMarkAsReviewed,
} from '@liscio/api';
import ConfirmationModal from 'common/ConfirmationModal';
import { AlertMessage } from 'utilities/utils';

export interface MarkAsReviewedConfirmationModalProps {
  fileUuid: string;
  isOpen: boolean;
  onClose: () => void;
}

export const MarkAsReviewedConfirmationModal = ({
  fileUuid,
  isOpen,
  onClose,
}: MarkAsReviewedConfirmationModalProps) => {
  const queryClient = useQueryClient();

  const { mutateAsync: markAsReviewed, isLoading } =
    usePutApiV5FilesIdMarkAsReviewed();

  const handleMarkAsReviewed = async () => {
    try {
      await markAsReviewed({ id: fileUuid });
      // Refetch the toolbar "files need review" count. invalidateQueries marks
      // the query stale and refetches its active observer (the count button),
      // whereas removeQueries can drop the cache without reliably re-running the
      // mounted query. LISD-2500.
      queryClient.invalidateQueries({
        queryKey: getGetApiV5FilesPendingReviewQueryKey(),
      });
      onClose();
    } catch {
      // Surface the failure instead of swallowing it; keep the modal open so
      // the user can retry. LISD-2500.
      AlertMessage(
        'error',
        'Failed to mark file as reviewed. Please try again.',
        3000,
      );
    }
  };

  return (
    <ConfirmationModal
      isOpen={isOpen}
      headerText="Confirm mark as reviewed"
      messageText="Do you want to mark this file as reviewed?"
      noBtnClick={onClose}
      yesBtnClick={handleMarkAsReviewed}
      yesBtnText="Yes, mark as reviewed"
      noBtnText="No"
      disableYesBtn={isLoading}
      disableNoBtn={isLoading}
    />
  );
};
